import { useState } from 'react'
import axios from 'axios'
import { BASE_URL } from '../apiConfig'
import Cookies from 'js-cookie'
import { toast } from 'sonner'
import { useTranslation } from '../LanguageContext'
import { PackagePlus, X, Minus, Plus } from 'lucide-react'

export default function AugmentStockModal({ product, onClose, onUpdated }) {
  const { t } = useTranslation()
  const token = Cookies.get('WEB_SSR')
  const [saving, setSaving] = useState(false)
  const [quantity, setQuantity] = useState(1)
  const [variantQtys, setVariantQtys] = useState(
    (product.variants || []).reduce((acc, v) => ({ ...acc, [v.id]: 1 }), {})
  )

  const updateVariantQty = (id, val) => {
    const num = parseInt(val) || 0
    setVariantQtys(prev => ({ ...prev, [id]: Math.max(0, num) }))
  }

  const handleSubmit = async () => {
    setSaving(true)
    try {
      let payload
      if (product.has_variants) {
        const variants = Object.entries(variantQtys)
          .filter(([, qty]) => qty > 0)
          .map(([productVariantId, qty]) => ({ product_variant_id: Number(productVariantId), quantity: qty }))
        if (variants.length === 0) {
          toast.error(t('products.stock.no_variant_qty'))
          setSaving(false)
          return
        }
        payload = { variants }
      } else {
        payload = { quantity }
      }

      const response = await axios.post(`${BASE_URL}/api/products/${product.id}/augment-stock`, payload, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      })

      if (response.data.success) {
        toast.success(t('products.stock.success'))
        window.dispatchEvent(new Event('stock-updated'))
        onUpdated?.()
        onClose()
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('products.stock.error'))
    } finally {
      setSaving(false)
    }
  }

  const getVariantLabel = (variant) => {
    const parts = []
    if (variant.color) parts.push(variant.color)
    if (variant.size) parts.push(variant.size)
    return parts.join(' / ') || `#${variant.id}`
  }

  const getVariantColor = (variant) => {
    if (variant.value_color) {
      return (
        <span
          className="inline-block h-4 w-4 rounded-full border border-gray-300 shrink-0"
          style={{ backgroundColor: variant.value_color }}
        />
      )
    }
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl dark:bg-gray-800" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-100 p-2 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
              <PackagePlus className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{t('products.stock.title')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{product.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4">
          {product.has_variants && product.variants?.length > 0 ? (
            <div className="space-y-3">
              {(product.variants || []).map((variant) => (
                <div key={variant.id} className="flex items-center gap-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                  {getVariantColor(variant)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{getVariantLabel(variant)}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('products.stock.current')}: {variant.stock} {t('table.pcs')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => updateVariantQty(variant.id, (variantQtys[variant.id] || 0) - 1)}
                      className="rounded-lg border border-gray-300 p-1.5 text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <input
                      type="number"
                      min="0"
                      value={variantQtys[variant.id] || 0}
                      onChange={e => updateVariantQty(variant.id, e.target.value)}
                      className="w-16 rounded-lg border border-gray-200 px-2 py-1.5 text-center text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    />
                    <button
                      type="button"
                      onClick={() => updateVariantQty(variant.id, (variantQtys[variant.id] || 0) + 1)}
                      className="rounded-lg border border-gray-300 p-1.5 text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('products.stock.current')}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{product.qty || 0} {t('table.pcs')}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="rounded-lg border border-gray-300 p-1.5 text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-16 rounded-lg border border-gray-200 px-2 py-1.5 text-center text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                />
                <button
                  type="button"
                  onClick={() => setQuantity(quantity + 1)}
                  className="rounded-lg border border-gray-300 p-1.5 text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            {t('invoices.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? t('invoices.saving') : t('products.stock.augment')}
          </button>
        </div>
      </div>
    </div>
  )
}
